import type { ReactNode } from "react";
import { useTheme } from "@/theme/ThemeProvider";
import { Text, View, type ViewStyle } from "react-native";

export type DataTableColumn<T> = {
  key: string;
  header: string;
  width?: number | `${number}%`;
  minWidth?: number;
  /** 0 hält Status und Aktionen auf ihrer Breite. */
  flexShrink?: number;
  /** flexGrow der flexiblen Spalten. */
  flex?: number;
  /** Zeilen für umbrechende Spalten. Standard 1. */
  lines?: number;
  align?: "left" | "right";
  render: (row: T) => ReactNode;
};

/**
 * Einfache Tabelle. `dense` nutzt table.rowDense, `touch` table.rowTouch.
 */
export function DataTable<T>({
  columns,
  rows,
  rowKey,
  density = "dense",
  rowHeight: rowHeightProp,
  empty,
  style,
  label,
}: {
  columns: DataTableColumn<T>[];
  rows: readonly T[];
  rowKey: (row: T) => string;
  density?: "dense" | "touch";
  /** Überschreibt das Dichte-Token, z. B. table.rowCase. */
  rowHeight?: number;
  empty?: React.ReactNode;
  style?: ViewStyle;
  label?: string;
}) {
  const { theme } = useTheme();
  const rowHeight =
    rowHeightProp ?? (density === "touch" ? theme.table.rowTouch : theme.table.rowDense);

  return (
    <View accessibilityLabel={label} role="table" style={style}>
      <View
        role="row"
        style={{
          flexDirection: "row",
          alignItems: "center",
          minHeight: rowHeight,
          borderBottomWidth: 1,
          borderBottomColor: theme.color.border,
          paddingHorizontal: theme.space.sm,
          gap: theme.space.sm,
        }}
      >
        {columns.map((column) => (
          <View key={column.key} accessibilityRole="header" role="columnheader" style={cellStyle(column)}>
            <Text
              style={{
                color: theme.color.muted,
                fontSize: theme.fontSize.tiny,
                fontWeight: "700",
                letterSpacing: 0.35,
                textTransform: "uppercase",
                textAlign: column.align ?? "left",
              }}
              numberOfLines={column.lines ?? 1}
            >
              {column.header}
            </Text>
          </View>
        ))}
      </View>
      {rows.length === 0 ? (
        empty ?? null
      ) : (
        rows.map((row) => (
          <View
            key={rowKey(row)}
            role="row"
            style={{
              flexDirection: "row",
              alignItems: "center",
              minHeight: rowHeight,
              borderBottomWidth: 1,
              borderBottomColor: theme.color.border,
              paddingHorizontal: theme.space.sm,
              gap: theme.space.sm,
            }}
          >
            {columns.map((column) => (
              <View key={column.key} role="cell" style={cellStyle(column)}>
                <CellValue align={column.align} lines={column.lines}>
                  {column.render(row)}
                </CellValue>
              </View>
            ))}
          </View>
        ))
      )}
    </View>
  );
}

function cellStyle<T>(column: DataTableColumn<T>): ViewStyle {
  return {
    flex: column.flex ?? (column.width == null ? 1 : undefined),
    width: column.width,
    minWidth: column.minWidth ?? 0,
    flexShrink: column.flexShrink,
  };
}

function CellValue({
  align,
  lines,
  children,
}: {
  align?: "left" | "right";
  lines?: number;
  children: ReactNode;
}) {
  const { theme } = useTheme();
  if (typeof children === "string" || typeof children === "number") {
    return (
      <Text
        style={{
          color: theme.color.text,
          fontSize: theme.fontSize.small,
          fontWeight: "600",
          textAlign: align ?? "left",
        }}
        numberOfLines={lines ?? 1}
      >
        {children}
      </Text>
    );
  }
  return <>{children}</>;
}
